import { FC, ReactNode, useEffect, useMemo, Children, isValidElement } from 'react';
import { MenuIcon } from './menu/MenuIcon';
import { ChevronDown, Circle } from 'lucide-react';
import { useMenuGroup, MenuGroupProvider } from './menu/MenuGroupContext';
import { useCurrentPath } from './menu/useCurrentPath';
import { extractChildRoutes } from './menu/extractChildRoutes';

type Props = {
  to: string;
  title: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  isActive?: boolean;
  depth?: number;
  children?: ReactNode;
};

function findActiveChildTitle(children: ReactNode, currentPath: string): string | null {
  let result: string | null = null;
  Children.forEach(children, (child) => {
    if (result) return;
    if (!isValidElement(child)) return;
    const props = child.props as Record<string, unknown>;
    if (typeof props.title === 'string' && props.children) {
      const routes = extractChildRoutes(child as ReactNode);
      if (routes.includes(currentPath)) {
        result = props.title as string;
      }
    }
  });
  return result;
}

const AsideMenuItemWithSub: FC<Props> = ({
  children,
  title,
  icon,
  hasBullet = false,
  depth = 0,
}) => {
  const { openId, setOpenId } = useMenuGroup();
  const currentPath = useCurrentPath();

  const allChildRoutes = useMemo(() => extractChildRoutes(children), [children]);
  const hasActiveChild = allChildRoutes.includes(currentPath);

  const nestedDefaultOpenId = useMemo(() => {
    if (!hasActiveChild) return null;
    return findActiveChildTitle(children, currentPath);
  }, [children, currentPath, hasActiveChild]);

  useEffect(() => {
    if (hasActiveChild) {
      setOpenId(title);
    }
  }, []);

  const isOpen = openId === title;
  const paddingLeft = depth > 0 ? 14 + depth * 14 : 14;

  const handleToggle = () => {
    setOpenId(isOpen ? null : title);
  };

  return (
    <div>
      <button
        onClick={handleToggle}
        className={`
          group w-full flex items-center gap-3 px-3 py-3 mx-2 rounded-lg text-[13.5px] font-medium
          transition-all duration-200 ease-out cursor-pointer
          ${isOpen
            ? 'bg-white/10 text-white'
            : hasActiveChild
              ? 'bg-white/5 text-white'
              : 'text-slate-200 hover:bg-white/10 hover:text-white'
          }
        `}
        style={{ paddingLeft: `${paddingLeft}px`, width: 'calc(100% - 16px)' }}
      >
        {hasBullet && (
          <Circle
            size={6}
            className={`flex-shrink-0 fill-current ${
              hasActiveChild ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'
            }`}
          />
        )}
        {icon && (
          <span
            className={`
              flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0
              transition-all duration-200
              ${isOpen || hasActiveChild
                ? 'bg-white/20 text-white'
                : 'bg-white/[0.07] text-slate-300 group-hover:bg-white/[0.12] group-hover:text-white'
              }
            `}
          >
            <MenuIcon icon={icon} size={20} />
          </span>
        )}
        <span className="truncate flex-1 text-left leading-snug">{title}</span>
        <ChevronDown
          size={16}
          className={`
            flex-shrink-0 text-slate-400 transition-transform duration-300 ease-out
            ${isOpen ? 'rotate-180 text-slate-200' : ''}
          `}
        />
      </button>
      <div
        className={`
          overflow-hidden transition-all duration-300 ease-out
          ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        <div className="py-1 ml-4 border-l border-white/10">
          <MenuGroupProvider defaultOpenId={nestedDefaultOpenId}>
            {children}
          </MenuGroupProvider>
        </div>
      </div>
    </div>
  );
};

export { AsideMenuItemWithSub };
